import { maxValue, minValue, numeric, required } from '@vuelidate/validators'
import router from '@/router'
import type { Config } from './tour'
import { useDevStore } from '@/stores/dev/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useCtaStore } from '@/stores/cta/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { computed } from 'vue'
import { i18n } from '@/core/i18n'
import { useExtensionStore } from '@/stores/extension/store'
import { nameUnit } from '@/validations/validatorUnits'

/*
  Requirements:
    - 1 “Call“ extension
    - Classic or Pro subscription
  Steps:
    - Edit extension
    - Edit forwarding
    - If is simple forwarding:
      - Say it’s simple forwarding but we will do extended now
      - Go to extended forwarding
    - Extended forwarding
      - Create a profile
        - Profile name (out of office)
        - Profile color (red)
        - Add step
          - Step name (Announcement)
          - Audio file
          - Save step
        - Save profile
      - Create another profile
        - Profile name (in office)
        - Profile color (green)
        - Show activate music switch (leave it turned off)
        - Add step
          - Step name (Ring devices)
          - All devices
          - 20 seconds
          - Save step
        - Add another step
          - Step name (Announcement)
          - Audio file
          - Save step
        - Save profile
      - Set the in office through the list view
        - Add time slot
        - Choose profile (in office)
        - Choose weekdays (working days)
        - Choose the time (working hours)
        - Save addition
        - Show delete buttons (no need to click them)
      - Set the out of office through the calendar view
        - Switch to calendar view
        - Select out of office
        - Allow user to click where they want, to activate it (press next to continue)
        - Show delete button (no need to click it)
      - Save extended forwarding
    - Save extension
  - Show icon of extended forwarding in the overview
*/

export const CTAExtendedForwardingSetupConfig: Config = {
  onStart() {
    // Redirect to overview
    router.push('/').catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        throw error
      }
    })

    // Force re-render to reset the view (if in edit extensions mode, the add button is hidden)
    useDevStore().forceRerender()
  },
  steps: [
    // Show requirements
    {
      delay: 200,
      title: 'tutorial.cta.extendedForwardingSetup.intro.title',
      text: 'tutorial.cta.extendedForwardingSetup.intro.text',
      showNextButton: true,
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      canGoToNext() {
        // Check if is not Basic edition

        const edition = useCtaStore().getEdition

        const isBasicEdition = edition === 'basic'

        if (isBasicEdition) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.extendedForwardingSetup.intro.requirements.basicEdition.invalid'
              )
            ),
          })

          return false
        }

        // Check has at least 1 extension already set up

        const extensions = useExtensionsStore().list

        const extensionsOfTypeCall = extensions.filter((e) =>
          ['default'].includes(e.type)
        )

        const hasExtensions = extensionsOfTypeCall.length

        if (!hasExtensions) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.extendedForwardingSetup.intro.requirements.hasExtensionOfTypeCall.invalid'
              )
            ),
          })

          return false
        }

        return true
      },
    },
    // Select extension to add the extended forwarding
    {
      title: 'tutorial.cta.extendedForwardingSetup.selectExtension.title',
      text: 'tutorial.cta.extendedForwardingSetup.selectExtension.text',
      attachElement: '[data-test=overview-section-extension]',
      nextOnClickElements: [
        '[data-extension-type=default] [data-test=edit-extension]',
      ],
      highlightElements: [
        '[data-extension-type=default] [data-test=edit-extension]',
      ],
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-extension-type]:not([data-extension-type=default]) [data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-night-switch]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
      attachPosition: 'top',
    },
    // Extension: edit forwarding
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.editForwarding.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.editForwarding.text',
      attachElement: '[data-test=forwarding-path]',
      nextOnClickElements: ['[data-test=forwarding-path]'],
      attachPosition: 'top',
    },
    // Extension: simple forwarding: switch to extended forwarding
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.switchToExtendedForwarding.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.switchToExtendedForwarding.text',
      showOn() {
        const extension = useExtensionStore().get

        const isExtendedCallForwarding =
          extension?.forwardingType === 'extended_call_forwarding'

        return !isExtendedCallForwarding
      },
      attachElement: '[data-test=simple-forwarding]',
      preventClickInElements: [
        '[data-test=simple-forwarding] :is(button,input,select,a):not([data-test=forwarding-switch-button])',
      ],
      preventEnterAndTabInElements: [
        '[data-test=simple-forwarding] :is(button,input,select,a):not([data-test=forwarding-switch-button])',
      ],
      highlightElements: ['[data-test=forwarding-switch-button]'],
      nextOnClickElements: ['[data-test=forwarding-switch-button]'],
      attachPosition: 'top',
    },
    // Extension: simple forwarding: switch to extended forwarding: accept modal
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.modalSwitchToExtendedForwarding.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.modalSwitchToExtendedForwarding.text',
      showOn() {
        const extension = useExtensionStore().get

        const isExtendedCallForwarding =
          extension?.forwardingType === 'extended_call_forwarding'

        return !isExtendedCallForwarding
      },
      attachElement: '[data-test=modal-primary-button]',
      nextOnClickElements: ['[data-test=modal-primary-button]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.addProfile1.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.addProfile1.text',
      attachElement: '[data-test=new-profile-btn]',
      nextOnClickElements: ['[data-test=new-profile-btn]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile out of office: profile name
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.name.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.name.text',
      attachElement: '#profileName',
      watchValueOfElements: ['#profileName'],
      preventEnterAndTabInElements: ['#profileName'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>('#profileName')

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile out of office: profile color
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.color.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.color.text',
      attachElement: '[data-test=profile-color]',
      watchValueOfElements: ['[data-test=profile-color] input'],
      preventEnterAndTabInElements: ['[data-test=profile-color] input'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=profile-color] input:checked'
        )

        return !!element
      },
    },
    // Extension: extended forwarding: add profile out of office: add step
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.addStep1.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.addStep1.text',
      attachElement: '[data-test=add-step-btn]',
      nextOnClickElements: ['[data-test=add-step-btn]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile out of office: add step announcement: step name
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.name.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.name.text',
      attachElement: '[data-test=stepData-name]',
      watchValueOfElements: ['[data-test=stepData-name]'],
      preventEnterAndTabInElements: ['[data-test=stepData-name]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile out of office: add step announcement: step type
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.type.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.type.text',
      attachElement: '[data-test=stepData-type]',
      watchValueOfElements: ['[data-test=stepData-type]'],
      preventEnterAndTabInElements: ['[data-test=stepData-type]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-type]'
        )

        // Check that the step type is set to "Audio File"
        return element?.value === 'prompt'
      },
    },
    // Extension: extended forwarding: add profile out of office: add step announcement: step audio file
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.audioFile.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.audioFile.text',
      attachElement: '[data-test=promptId]',
      watchValueOfElements: ['[data-test=promptId]'],
      preventEnterAndTabInElements: ['[data-test=promptId]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=promptId]'
        )

        // Check that the step audio file is set to "Standard notification"
        return element?.value === '1'
      },
    },
    // Extension: extended forwarding: add profile out of office: add step announcement: step notification
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.notification.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.notification.text',
      attachElement: '[data-test=notificationType]',
      watchValueOfElements: ['[data-test=notificationType]'],
      preventEnterAndTabInElements: ['[data-test=notificationType]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=notificationType]'
        )

        // Check that the step notification is set to "None"
        return element?.value === 'none'
      },
    },
    // Extension: extended forwarding: add profile out of office: add step announcement: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.step1.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile out of office: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile1.save.text',
      waitForElements: [
        '[data-test=add-edit-profile-page][data-is-loading=false]',
      ],
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.addProfile2.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.addProfile2.text',
      attachElement: '[data-test=new-profile-btn]',
      nextOnClickElements: ['[data-test=new-profile-btn]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile in the office: profile name
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.name.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.name.text',
      attachElement: '#profileName',
      watchValueOfElements: ['#profileName'],
      preventEnterAndTabInElements: ['#profileName'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>('#profileName')

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile in the office: profile color
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.color.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.color.text',
      attachElement: '[data-test=profile-color]',
      watchValueOfElements: ['[data-test=profile-color] input'],
      preventEnterAndTabInElements: ['[data-test=profile-color] input'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=profile-color] input:checked'
        )

        return !!element
      },
    },
    // Extension: extended forwarding: add profile in the office: add step
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.addStep1.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.addStep1.text',
      attachElement: '[data-test=add-step-btn]',
      nextOnClickElements: ['[data-test=add-step-btn]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile in the office: add step ring: step name
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.name.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.name.text',
      attachElement: '[data-test=stepData-name]',
      watchValueOfElements: ['[data-test=stepData-name]'],
      preventEnterAndTabInElements: ['[data-test=stepData-name]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile in the office: add step ring: step type
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.type.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.type.text',
      attachElement: '[data-test=stepData-type]',
      watchValueOfElements: ['[data-test=stepData-type]'],
      preventEnterAndTabInElements: ['[data-test=stepData-type]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-type]'
        )

        // Check that the step type is set to "All Devices"
        return element?.value === 'allDevices'
      },
    },
    // Extension: extended forwarding: add profile in the office: add step ring: step ring time
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.ringTime.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.ringTime.text',
      attachElement: '[data-test=ringtimeValue]',
      watchValueOfElements: ['[data-test=ringtimeValue]'],
      preventEnterAndTabInElements: ['[data-test=ringtimeValue]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=ringtimeValue]'
        )

        const value = element?.value

        if (
          !required.$validator(value, undefined, undefined) ||
          !numeric.$validator(value, undefined, undefined) ||
          !minValue(1).$validator(value, undefined, undefined) ||
          !maxValue(300).$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile in the office: add step ring: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step1.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile in the office: add step
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.addStep2.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.addStep2.text',
      attachElement: '[data-test=add-step-btn]',
      nextOnClickElements: ['[data-test=add-step-btn]'],
      attachPosition: 'top',
    },

    // Extension: extended forwarding: add profile in the office: add step announcement: step name
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.name.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.name.text',
      attachElement: '[data-test=stepData-name]',
      watchValueOfElements: ['[data-test=stepData-name]'],
      preventEnterAndTabInElements: ['[data-test=stepData-name]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Extension: extended forwarding: add profile in the office: add step announcement: step type
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.type.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.type.text',
      attachElement: '[data-test=stepData-type]',
      watchValueOfElements: ['[data-test=stepData-type]'],
      preventEnterAndTabInElements: ['[data-test=stepData-type]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=stepData-type]'
        )

        // Check that the step type is set to "Audio File"
        return element?.value === 'prompt'
      },
    },
    // Extension: extended forwarding: add profile in the office: add step announcement: step audio file
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.audioFile.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.audioFile.text',
      attachElement: '[data-test=promptId]',
      watchValueOfElements: ['[data-test=promptId]'],
      preventEnterAndTabInElements: ['[data-test=promptId]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=promptId]'
        )

        // Check that the step audio file is set to "Standard notification"
        return element?.value === '1'
      },
    },
    // Extension: extended forwarding: add profile in the office: add step announcement: step notification
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.notification.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.notification.text',
      attachElement: '[data-test=notificationType]',
      watchValueOfElements: ['[data-test=notificationType]'],
      preventEnterAndTabInElements: ['[data-test=notificationType]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=notificationType]'
        )

        // Check that the step notification is set to "None"
        return element?.value === 'none'
      },
    },
    // Extension: extended forwarding: add profile in the office: add step announcement: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.step2.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add profile in the office: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.profile2.save.text',
      waitForElements: [
        '[data-test=add-edit-profile-page][data-is-loading=false]',
      ],
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: show profiles
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.showProfiles.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.showProfiles.text',
      waitForElements: ['[data-test=extended-forwarding-page]'],
      attachElement: '[data-test=profiles-section]',
      preventClickInElements: [
        '[data-test=edit-profile]',
        '[data-test=new-profile-btn]',
      ],
      preventEnterAndTabInElements: [
        '[data-test=edit-profile]',
        '[data-test=new-profile-btn]',
      ],
      attachPosition: 'top',
      showNextButton: true,
    },
    // Extension: extended forwarding: add list view time slot
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.addTimeSlotListView.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.addTimeSlotListView.text',
      attachElement: '[data-test=add-timeslot]',
      nextOnClickElements: ['[data-test=add-timeslot]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add list view time slot: select profile
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.profile.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.profile.text',
      attachElement: '[data-test=profile-select]',
      preventEnterAndTabInElements: ['[data-test=profile-select]'],
      watchValueOfElements: ['[data-test=profile-select]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=profile-select]'
        )

        return Boolean(element?.value)
      },
    },
    // Extension: extended forwarding: add list view time slot: select days
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.days.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.days.text',
      attachElement: '[data-test=selected-days]',
      preventEnterAndTabInElements: ['[data-test=selected-days] input'],
      watchValueOfElements: ['[data-test=selected-days] input'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=selected-days] input:checked'
        )

        return !!element
      },
    },
    // Extension: extended forwarding: add list view time slot: select time
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.time.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.time.text',
      attachElement: '[data-test=select-time]',
      preventEnterAndTabInElements: ['[data-test=select-time] select'],
      watchValueOfElements: ['[data-test=select-time] select'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const startHoursElement = document.querySelector<HTMLSelectElement>(
          '[data-test=startHours]'
        )

        const startMinutesElement = document.querySelector<HTMLSelectElement>(
          '[data-test=startMinutes]'
        )

        const endHoursElement = document.querySelector<HTMLSelectElement>(
          '[data-test=endHours]'
        )

        const endMinutesElement = document.querySelector<HTMLSelectElement>(
          '[data-test=endMinutes]'
        )

        return (
          Boolean(startHoursElement?.value) &&
          Boolean(startMinutesElement?.value) &&
          Boolean(endHoursElement?.value) &&
          Boolean(endMinutesElement?.value)
        )
      },
    },
    // Extension: extended forwarding: add list view time slot: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.timeSlotListView.save.text',
      attachElement: '[data-test=add-timeslot-btn]',
      preventEnterAndTabInElements: ['[data-test=add-timeslot-btn]'],
      nextOnClickElements: ['[data-test=add-timeslot-btn]'],
      attachPosition: 'top',
    },
    // Extension: extended forwarding: add calendar view time slot: switch to calendar view
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.switchToCalendarView.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.switchToCalendarView.text',
      attachElement: '[data-test=forwarding-tab-select]',
      preventEnterAndTabInElements: ['[data-test=forwarding-tab-select]'],
      watchValueOfElements: ['[data-test=forwarding-tab-select]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=forwarding-tab-select]'
        )

        // Check that has selected the calendar view
        return element?.value === 'calendarView'
      },
    },
    // Extension: extended forwarding: add calendar view time slot: select profile
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.calendarView.profile.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.calendarView.profile.text',
      attachElement: '[data-test=calendar-profile-buttons]',
      preventEnterAndTabInElements: ['[data-test=calendar-profile-buttons]'],
      watchValueOfElements: ['[data-test=calendar-profile-button] input'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=calendar-profile-button] input:checked'
        )

        // Check that a profile has been selected
        return !!element
      },
    },
    // Extension: extended forwarding: add calendar view time slot: fill time sheet
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.calendarView.timesheet.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.calendarView.timesheet.text',
      attachElement: '[data-test=timesheet]',
      attachPosition: 'top',
      showNextButton: true,
    },
    // Extension: extended forwarding: save
    {
      title:
        'tutorial.cta.extendedForwardingSetup.extension.forwarding.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.forwarding.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Extension: save
    {
      title: 'tutorial.cta.extendedForwardingSetup.extension.save.title',
      text: 'tutorial.cta.extendedForwardingSetup.extension.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      attachPosition: 'top',
    },
    // Overview: show where the shortcut icon is for editing an extension's forwarding
    {
      title:
        'tutorial.cta.extendedForwardingSetup.overview.showEditForwardingIcon.title',
      text: 'tutorial.cta.extendedForwardingSetup.overview.showEditForwardingIcon.text',
      attachElement: '[data-test=overview-section-extension]',
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      highlightElements: ['[data-test=edit-call-forwarding]'],
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-night-switch]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
      attachPosition: 'top',
      showFinishButton: true,
    },
  ],
}
